import { SHIPMENT_PRICE, SUBSCRIPTION_DISCOUNT } from "../config/config";
import { bundleAwareCartItems } from "./bundleAwareCartItems";

export const calculatePrice = (price, quantity, isSubscribed, promoCodes = []) => {
  let discountedPrice = price;
  
  // Apply subscription discount first if applicable
  if (isSubscribed) {
    discountedPrice *= (1 - SUBSCRIPTION_DISCOUNT);
  }
  
  // Apply promo codes only if any are active
  if (promoCodes.length > 0) {
    let eligiblePromoCodes = promoCodes.filter(promo => promo.is_affiliate !== false && promo.type !== 'percent');

    // Sort promo codes: percent discounts first, then fixed
    eligiblePromoCodes = [...promoCodes].sort((a, b) => {
      if (a.discount_type === 'percent' && b.discount_type === 'fixed') return -1;
      if (a.discount_type === 'fixed' && b.discount_type === 'percent') return 1;
      return 0;
    });
    
    for (const promo of eligiblePromoCodes) {
      if (promo.discount_amount) {
        if (promo.discount_type === 'percent') {
          // Apply percentage discount
          discountedPrice *= (1 - promo.discount_amount / 100);
        } else if (promo.discount_type === 'fixed') {
          // Apply fixed discount per item
          // Note: This divides the fixed discount across quantity
          const fixedDiscountPerItem = promo.discount_amount / quantity;
          discountedPrice = Math.max(0, discountedPrice - fixedDiscountPerItem);
        }
      }
    }
  }
  
  const rawTotal = discountedPrice * quantity;
  return formatPrice(rawTotal);
};

export const calculateSubtotal = (cartItems = [], promoCodes = []) => {
  const bundleAwareItems = bundleAwareCartItems(cartItems);
  
  return bundleAwareItems
    .reduce((total, item) => {
      if (item.type === 'bundle') {
        const hasSubscription = item.products.some(product => product.subscription);
        return total + calculatePrice(item.bundleTotal, item.quantity, hasSubscription, promoCodes);
      } else {
        return total + calculatePrice(item.price, item.quantity, item.subscription, promoCodes);
      }
    }, 0);
};
export const formatPrice = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

export const formatDisplayPrice = (num) => `$${formatPrice(num).toFixed(2)}`;

export const calculateRawTotal = (cartItems = [], promoCodes = []) => {
  const subtotal = cartItems
    .filter(item => item.active)
    .reduce(
      (total, item) =>
        total + calculatePrice(item.price, item.quantity, item.subscription, []),
      0
    );

  const freeShipping = promoCodes.some(promo => promo.free_shipping);
  
  return formatPrice(subtotal + (freeShipping ? 0 : SHIPMENT_PRICE));
};

export const calculateTotal = (total, promoCodes = []) => {
  let discounted = total;
  let freeShipping = false;

  // Sort promo codes: percent discounts first, then fixed
  const sortedPromos = [...promoCodes].sort((a, b) => {
    if (a.discount_type === 'percent' && b.discount_type === 'fixed') return -1;
    if (a.discount_type === 'fixed' && b.discount_type === 'percent') return 1;
    return 0;
  });

  // First pass: apply all percentage discounts (respecting max_order_total)
  for (const promo of sortedPromos) {
    if (promo.discount_type === 'percent' && promo.discount_amount) {
      // Calculate eligible amount for percentage discount
      const eligibleAmount = promo.max_order_total ? Math.min(total, promo.max_order_total) : total;
      const percentDiscount = eligibleAmount * (promo.discount_amount / 100);
      discounted = Math.max(0, discounted - percentDiscount);
    }
    if (promo.free_shipping) {
      freeShipping = true;
    }
  }

  // Second pass: apply all fixed discounts (respecting max_order_total)
  for (const promo of sortedPromos) {
    if (promo.discount_type === 'fixed' && promo.discount_amount) {
      // Calculate eligible amount after other discounts
      const eligibleAmount = promo.max_order_total ? Math.min(total, promo.max_order_total) : discounted;
      // Cap the fixed discount to not exceed the eligible amount
      const applicableDiscount = Math.min(promo.discount_amount, eligibleAmount);
      discounted = Math.max(0, discounted - applicableDiscount);
    }
  }

  // Add shipping if not free
  if (!freeShipping) {
    discounted += SHIPMENT_PRICE;
  }

  const totalDollars = formatPrice(discounted);
  const totalCents = Math.round(totalDollars * 100);

  return {
    discountedTotal: totalDollars,
    discountedTotalCents: totalCents
  };
};

// Additional helper function for calculating cart total with all discounts
export const calculateCartTotal = (cartItems = [], promoCodes = []) => {
  const subtotal = calculateSubtotal(cartItems, []);
  let total = subtotal;
  let totalDiscount = 0;
  let freeShipping = false;
  
  // Apply subscription discounts
  const subscriptionDiscount = cartItems
    .filter(item => item.active && item.subscription)
    .reduce((discount, item) => {
      return discount + (item.price * item.quantity * SUBSCRIPTION_DISCOUNT);
    }, 0);
  
  total -= subscriptionDiscount;
  totalDiscount += subscriptionDiscount;
  
  // Sort and apply promo codes
  const sortedPromos = [...promoCodes].sort((a, b) => {
    if (a.discount_type === 'percent' && b.discount_type === 'fixed') return -1;
    if (a.discount_type === 'fixed' && b.discount_type === 'percent') return 1;
    return 0;
  });
  
  // Apply percentage discounts (respecting max_order_total)
  for (const promo of sortedPromos) {
    if (promo.discount_type === 'percent' && promo.discount_amount) {
      // Calculate eligible amount for percentage discount
      const eligibleAmount = promo.max_order_total ? Math.min(subtotal, promo.max_order_total) : total;
      const discountAmount = eligibleAmount * (promo.discount_amount / 100);
      total -= discountAmount;
      totalDiscount += discountAmount;
    }
    if (promo.free_shipping) {
      freeShipping = true;
    }
  }

  // Apply fixed discounts (respecting max_order_total)
  for (const promo of sortedPromos) {
    if (promo.discount_type === 'fixed' && promo.discount_amount) {
      // Calculate eligible amount after other discounts
      const eligibleAmount = promo.max_order_total ? Math.min(subtotal, promo.max_order_total) : total;
      // Cap the fixed discount to not exceed the eligible amount
      const applicableDiscount = Math.min(promo.discount_amount, Math.min(eligibleAmount, total));
      total -= applicableDiscount;
      totalDiscount += applicableDiscount;
    }
  }
  
  // Add shipping
  const shipping = freeShipping ? 0 : SHIPMENT_PRICE;
  total += shipping;
  
  return {
    subtotal: formatPrice(subtotal),
    subscriptionDiscount: formatPrice(subscriptionDiscount),
    promoDiscount: formatPrice(totalDiscount - subscriptionDiscount),
    shipping: formatPrice(shipping),
    total: formatPrice(total),
    freeShipping
  };
};